import { type ChangeEvent, type FormEvent } from "react";
import { RotateCcw, Save } from "lucide-react";
import { Button, Card, Input } from "../components/ui/index.js";
import {
  type PayrollUser,
  type PayrollProfileDraft,
  type PayrollProfilePeriod,
} from "./payrollProfileModel.js";

interface ProfileDraftFields {
  selectedUser: PayrollUser;
  selectedProfile: PayrollProfileDraft;
  updateProfileDraft: (patch: Partial<PayrollProfileDraft>) => void;
}

export function ProfileIdentityForm({
  selectedUser,
  selectedProfile,
  resolvingMissingPayslip,
  submitProfile,
  updateProfileDraft,
}: ProfileDraftFields & {
  resolvingMissingPayslip: boolean;
  submitProfile: (event: FormEvent) => void;
}) {
  return (
    <form
      id={`profile-${selectedUser.id}-identity`}
      onSubmit={submitProfile}
      noValidate
      className="w-full min-w-0 space-y-2"
    >
      <Card
        elevated
        padding="sm"
        className={
          resolvingMissingPayslip ? "ring-2 ring-warning/45" : undefined
        }
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            id={`profile-${selectedUser.id}-code`}
            label="Employee code"
            value={selectedProfile.payrollEmployeeCode}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateProfileDraft({
                payrollEmployeeCode: event.target.value.toLocaleUpperCase(),
              })
            }
            placeholder="Z0039"
            maxLength={40}
            className="uppercase tabular-nums"
          />
          <Input
            id={`profile-${selectedUser.id}-payroll-email`}
            label="Email"
            type="email"
            autoComplete="email"
            value={selectedProfile.payrollEmail}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateProfileDraft({ payrollEmail: event.target.value })
            }
            placeholder="payroll@example.com"
            maxLength={320}
          />
        </div>
      </Card>
      <Card elevated padding="sm">
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            id={`profile-${selectedUser.id}-start`}
            label="Start date"
            type="date"
            value={selectedProfile.payrollStartDate}
            readOnly
            className="tabular-nums"
          />
          <Input
            id={`profile-${selectedUser.id}-end`}
            label="End date"
            type="date"
            value={selectedProfile.payrollEndDate}
            readOnly
            className="tabular-nums"
          />
        </div>
      </Card>
    </form>
  );
}

export function ProfileContractSettings({
  selectedUser,
  selectedProfile,
  allowanceFromPeriod,
  updateProfileDraft,
  profileEdited,
  busy,
  saving,
  onReset,
}: ProfileDraftFields & {
  allowanceFromPeriod: PayrollProfilePeriod | undefined;
  profileEdited: boolean;
  busy: boolean;
  saving: boolean;
  onReset: () => void;
}) {
  const formId = `profile-${selectedUser.id}-identity`;
  return (
    <div className="mb-3 border-b border-border pb-3">
      <fieldset className="min-w-0">
        <legend className="pr-2 text-sm font-semibold text-foreground">
          Contract settings
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            id={`profile-${selectedUser.id}-vacation-allowance`}
            form={formId}
            label="Vacation allowance (days/year)"
            type="number"
            min="0"
            step="0.01"
            value={
              allowanceFromPeriod
                ? String(allowanceFromPeriod.annualHolidayEntitlement)
                : selectedProfile.payrollAnnualHolidayEntitlement
            }
            readOnly={Boolean(allowanceFromPeriod)}
            hint={
              allowanceFromPeriod
                ? "Set by the contract period below."
                : undefined
            }
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              if (allowanceFromPeriod) return;
              updateProfileDraft({
                payrollAnnualHolidayEntitlement: event.target.value,
              });
            }}
            className="tabular-nums"
          />
          <Input
            id={`profile-${selectedUser.id}-pdf-password`}
            form={formId}
            label="Payslip PDF password"
            type="password"
            autoComplete="new-password"
            value={selectedProfile.payrollPdfPassword}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateProfileDraft({
                payrollPdfPassword: event.target.value,
              })
            }
            placeholder={
              selectedUser.payrollPdfPasswordConfigured
                ? "Configured — enter a new password to replace"
                : "Enter at least 8 characters"
            }
            hint="One password per employee. Saved passwords are never shown."
            minLength={8}
            maxLength={127}
          />
        </div>
      </fieldset>
      <div className="mt-2 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          form={formId}
          variant="ghost"
          size="sm"
          disabled={!profileEdited || busy}
          onClick={() => {
            onReset();
          }}
        >
          <RotateCcw aria-hidden="true" size={15} /> Reset
        </Button>
        <Button
          type="submit"
          form={formId}
          size="sm"
          loading={saving}
          disabled={!profileEdited || busy}
        >
          <Save aria-hidden="true" size={15} /> Save current profile
        </Button>
      </div>
    </div>
  );
}
